import * as React from 'react'
import { Alert, Button, Dropdown, Form, Input, Menu, Radio, Spin, Tabs, Tooltip, Popconfirm, message } from 'antd'
import { get, set, isEmpty, cloneDeep, uniq, pick } from 'lodash'
import produce from 'immer'
import { showError, resourceNotFound, ValidationError, showClientNotifications } from 'helpers/errors'
import { getUserPermission, allowPricing, createFilterUserSuppliers } from 'helpers/auth'
import { tryParseFloat, createResetState } from 'helpers/utils'
import SelectProductCategories from 'containers/ProductCategories/Select'
import Select, { Option, getOptionProps } from 'elements/Select'
import InputNumber from 'elements/InputNumber'
import Drawer from 'elements/Drawer'
import Icon from 'elements/Icon'
import Comments from 'elements/Comments'
import { Row, Col } from 'elements/Grid'
import {
  createLabelFactory,
  createFieldFactory,
  createSaveDocumentItems,
  createSaveImage,
  createHandleSubmit,
  createSearchSuppliers,
  createDropdownRender,
  getChangedItems,
  validateBarcode,
  isReadOnly,
} from 'helpers/formViews'
import linkTargets from 'options/linkTargets'
import { t } from 'helpers/i18n'
import { PRIVATE_DATABASE } from 'options/products'
import { stopEvent } from 'helpers/events'
import { getStorageItem, setStorageItem } from 'helpers/localStorage'
import Documents from 'containers/Documents/ChildListView'
import FileUpload from 'elements/FileUpload'
import CustomerDetails from 'containers/Products/CustomerDetails'
import ProductAlternates from 'containers/Products/ProductAlternates'
import SupplierDetails from 'containers/Products/SupplierDetails'
import Checkbox from 'elements/Checkbox'
import InputImage from 'elements/InputImage'
import FormButtons from 'elements/FormButtons'
import Modal from 'elements/Modal'
import SelectProducts from 'containers/Products/Select'
import Help from 'elements/Help'
import { getCountries } from 'helpers/countries'

const reworkTypes = {
  New: 'New',
  Reworkable: 'Reworkable',
  Reworked: 'Reworked',
}

const copyOptionsCheckboxes = [
  'updateExistingInventoryRecords',
  'updateExistingProductLists',
  'deactivateOriginalProductRecord',
]

const getStorageKey = () => 'products.formView'

const InventoryFormView = linkTargets.inventoryRecord.formComponent

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
    this.saveImage = createSaveImage(this, 'Product')
    this.saveDocumentItems = createSaveDocumentItems(this, 'Product')
    this.searchSuppliers = createSearchSuppliers(this)
    this.resetState = createResetState(
      this,
      {
        copyOptions: {
          newProductReplacesOriginalProduct: false,
          updateExistingInventoryRecords: true,
          updateExistingProductLists: true,
          deactivateOriginalProductRecord: true,
        },
      },
      getStorageItem(getStorageKey(), {})
    )
  }

  async componentDidMount() {
    await this.resetState()
    await this.fetchItem()
  }

  saveState = () => setStorageItem(getStorageKey(), pick(this.state, ['copyOptions']))

  getCatalogTableName = () => this.props.filterDto?.catalogTableName ?? PRIVATE_DATABASE

  transformSaveItemParams = () => {
    const catalogTableName = this.getCatalogTableName()

    return catalogTableName !== PRIVATE_DATABASE
      ? { sourceCatalogTableName: catalogTableName, IsCatalogItem: true }
      : {}
  }

  getItemId = () => {
    const productId = [
      this.state.item?.id,
      this.props.linkTargetRecord?.productId,
      this.props.linkTargetRecord?.objectId,
    ].find(Boolean)

    if (productId) {
      const catalogTableName = this.getCatalogTableName()

      if (catalogTableName && catalogTableName !== PRIVATE_DATABASE) {
        return `${productId}?catalogTableName=${encodeURIComponent(catalogTableName)}`
      }

      return productId
    }

    return null
  }

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const catalogTableName = this.getCatalogTableName()
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({
                active: true,
                price: 0,
                packageSize: 1,
                defaultSupplierId: 0,
                leadTime: 0,
                oldUnitOfMeasureId: 0,
                packageUnitOfMeasure: 0,
                reworkType: reworkTypes.New,
              })
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
      ])

      const item = cloneDeep(responses[0].value.data)

      const [
        documentItems,
        image,
        customerDetailItems,
        productAlternateItems,
        supplierDetailItems,
        productCategories,
        suppliers,
        unitsOfMeasure,
      ] = await Promise.all([
        item.id
          ? this.props.getDocumentItems({
              request: {
                domainObjectId: item.id,
                domainObjectType: 'Product',
                documentType: 'ObjectDocument',
              },
            })
          : Promise.resolve(null),
        item.id && catalogTableName === PRIVATE_DATABASE
          ? this.props
              .getDocumentContents({
                request: {
                  domainObjectId: item.id,
                  domainObjectType: 'Product',
                  documentType: 'ObjectImage',
                  documentName: item.barcode,
                },
              })
              .catch(() => {})
          : Promise.resolve(null),
        item.id && this.props.userIsDcribDistributor
          ? this.props.getCustomerDetailItems(item.id)
          : Promise.resolve(null),
        item.id && catalogTableName === PRIVATE_DATABASE
          ? this.props.getProductAlternateItems(item.id)
          : Promise.resolve(null),
        item.id && this.props.tenantTypeIsTrms ? this.props.getSupplierDetailItems(item.id) : Promise.resolve(null),
        this.props.getProductCategories({ productCategoryIds: item.productCategoryIds }),
        this.props.getSuppliers(item.defaultSupplierId ? { supplierIds: [item.defaultSupplierId] } : undefined),
        this.props.getUnitsOfMeasure({
          active: 'Active',
          unitsOfMeasureIds: uniq([item.oldUnitOfMeasureId, item.packageUnitOfMeasure].filter(Boolean)),
        }),
      ])

      this.setState({
        item,
        fieldSettings: responses[1].value.data.fieldSettings,
        image: image?.value?.data,
        imageOriginal: image?.value?.data,
        documentItems: get(documentItems, 'value.data.items', []),
        documentItemsOriginal: get(documentItems, 'value.data.items', []),
        documentItemsTotals: get(documentItems, 'value.data.pageTotals', {}),
        customerDetailItems: get(customerDetailItems, 'value.data.items', []),
        customerDetailItemsOriginal: get(customerDetailItems, 'value.data.items', []),
        customerDetailItemsTotals: get(customerDetailItems, 'value.data.pageTotals', {}),
        productAlternateItems: get(productAlternateItems, 'value.data.items', []),
        productAlternateItemsOriginal: get(productAlternateItems, 'value.data.items', []),
        productAlternateItemsTotals: get(productAlternateItems, 'value.data.pageTotals', {}),
        supplierDetailItems: get(supplierDetailItems, 'value.data.items', []),
        supplierDetailItemsOriginal: get(supplierDetailItems, 'value.data.items', []),
        supplierDetailItemsTotals: get(supplierDetailItems, 'value.data.pageTotals', {}),
        productCategories: get(productCategories, 'value.data.items', []),
        suppliers: get(suppliers, 'value.data', {}),
        unitsOfMeasure: get(unitsOfMeasure, 'value.data.items', []),
        copyFromExistingBarcode: null,
      })

      if (item.id) {
        this.checkReworkedBarcode()
      }
    } catch (error) {
      showError({ error })
    }
  }

  saveCustomerDetailItems = async (productId) => {
    const { creating, updating, deleting } = getChangedItems(
      this.state.customerDetailItemsOriginal,
      this.state.customerDetailItems
    )

    if (!isEmpty(deleting)) {
      const response = await this.props.deleteCustomerDetailItems(
        productId,
        deleting.map((each) => each.id)
      )

      showClientNotifications({ response })
    }

    if (!isEmpty(updating)) {
      const response = await this.props.updateCustomerDetailItems(
        productId,
        updating.map((each) => ({
          ...each,
          productId,
          barcode: this.state.item.barcode,
        }))
      )

      showClientNotifications({ response })
    }

    if (!isEmpty(creating)) {
      const response = await this.props.createCustomerDetailItems(
        productId,
        creating.map(({ id, ...rest }) => ({
          ...rest,
          productId,
          barcode: this.state.item.barcode,
        }))
      )

      showClientNotifications({ response })
    }
  }

  saveProductAlternateItems = async (productId) => {
    const { creating, updating, deleting } = getChangedItems(
      this.state.productAlternateItemsOriginal,
      this.state.productAlternateItems
    )

    if (!isEmpty(creating)) {
      const response = await this.props.createProductAlternateItems(
        productId,
        creating.map(({ id, ...each }) => ({ ...each, productId }))
      )

      showClientNotifications({ response })
    }

    if (!isEmpty(updating)) {
      const response = await this.props.updateProductAlternateItems(
        productId,
        updating.map((each) => ({ ...each, productId }))
      )

      showClientNotifications({ response })
    }

    if (!isEmpty(deleting)) {
      const response = await this.props.deleteProductAlternateItems(
        productId,
        deleting.map((each) => each.id)
      )

      showClientNotifications({ response })
    }
  }

  saveSupplierDetailItems = async (productId) => {
    const { creating, updating, deleting } = getChangedItems(
      this.state.supplierDetailItemsOriginal,
      this.state.supplierDetailItems
    )

    if (!isEmpty(deleting)) {
      const response = await this.props.deleteSupplierDetailItems(
        productId,
        deleting.map((each) => each.id)
      )

      showClientNotifications({ response })
    }

    if (!isEmpty(updating)) {
      const response = await this.props.updateSupplierDetailItems(
        productId,
        updating.map((each) => ({
          ...each,
          productId,
          barcode: this.state.item.barcode,
          preferredSupplier: each.supplierId === this.state.item.defaultSupplierId,
        }))
      )

      showClientNotifications({ response })
    }

    if (!isEmpty(creating)) {
      const response = await this.props.createSupplierDetailItems(
        productId,
        creating.map(({ id, ...each }) => ({
          ...each,
          productId,
          barcode: this.state.item.barcode,
          preferredSupplier: each.supplierId === this.state.item.defaultSupplierId,
        }))
      )

      showClientNotifications({ response })
    }
  }

  saveItem = async (item) => {
    if (!item) {
      throw new Error('item is undefined')
    }

    const saved = item.id ? await this.props.updateItem(item) : await this.props.createItem(item)

    await Promise.all([
      this.saveDocumentItems(saved.value.data.id),
      this.saveImage(saved.value.data.id, item.barcode),
      this.props.userIsDcribDistributor ? this.saveCustomerDetailItems(saved.value.data.id) : Promise.resolve(null),
      this.saveProductAlternateItems(saved.value.data.id),
      this.props.tenantTypeIsTrms ? this.saveSupplierDetailItems(saved.value.data.id) : Promise.resolve(null),
    ])

    return saved
  }

  saveItemSuccess = (savedItem, saveItemParams) =>
    new Promise((resolve, reject) => {
      if (this.state.copyFromExistingBarcode) {
        const copyOptionsOnCancel = () => {
          this.setState({ copyOptionsVisible: false }, () => resolve(true))
        }

        const copyOptionsOnOk = () => {
          Modal.confirm({
            autoFocusButton: 'ok',
            title: t('confirmProductCopyOptions'),
            okType: 'primary',
            okText: t('yes'),
            cancelText: t('no'),
            onOk: async () => {
              try {
                this.setState({ copyOptionsLoading: true })

                const response = await this.props.migrate({
                  fromBarcode: this.state.copyFromExistingBarcode,
                  toBarcode: savedItem.barcode,
                  ...pick(this.state.copyOptions, copyOptionsCheckboxes),
                })

                showClientNotifications({ response })

                if (response.value.data.failureCount > 0) {
                  throw new Error()
                }

                this.setState({ copyOptionsVisible: false }, () => resolve(true))
              } catch (error) {
                showClientNotifications({ error })
                showError({ error })
              } finally {
                this.setState({ copyOptionsLoading: false })
              }
            },
          })
        }

        this.setState({ copyOptionsVisible: true, copyOptionsOnCancel, copyOptionsOnOk })
      } else {
        resolve(true)
      }
    })

  handleFileUpload = (fileList = []) => {
    if (fileList[0]) {
      this.setState({
        image: {
          contents: fileList[0].fileContents,
          fileType: fileList[0].fileType,
        },
        fileUploadDrawerVisible: false,
      })
    } else {
      this.setState({ fileUploadDrawerVisible: false })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        if (name === 'defaultSupplierId') {
          draft.supplierDetailItems.forEach((each) => {
            each.preferredSupplier = each.supplierId === value
          })
        }

        this.props.form.setFieldsValue({ [name]: value })
      })
    )

  checkReworkedBarcode = async () => {
    try {
      const { item } = this.state

      if (isEmpty(item.reworkedBarcode)) {
        this.setState({ reworkedDescription: '' })
        return
      }

      const reworked = await this.props
        .findByBarcode({
          barcode: item.reworkedBarcode,
          catalogTableName: '',
        })
        .then((r) => get(r, 'value.data', {}))

      this.setState({
        reworkedDescription:
          reworked.reworkType !== reworkTypes.Reworked ? t('notDefinedAsReworkedItem') : reworked.description,
      })
    } catch (error) {
      this.setState({ reworkedDescription: t('notDefinedAsReworkedItem') })
    }
  }

  handleActionsMenuClick = ({ key }) => {
    switch (key) {
      case 'copyToInventory':
        this.setState({
          copyToInventoryVisible: true,
          copyToInventorySaving: false,
        })
        break

      case 'copyFromExistingRecord':
        this.setState({ copyFromExistingVisible: true })
        break

      default:
        message.warn(t('underDevelopment'))
        break
    }
  }

  handleCopyFromExisting = async () => {
    try {
      this.setState({ copyFromExistingLoading: true })

      const { id, ...rest } = await this.props
        .getItem(this.state.copyFromExistingSelectedRecord.id)
        .then((r) => r.value.data)
        .then(cloneDeep)

      const [image, customerDetailItems, productAlternateItems, supplierDetailItems, productCategories] =
        await Promise.all([
          this.props
            .getDocumentContents({
              request: {
                domainObjectId: id,
                domainObjectType: 'Product',
                documentType: 'ObjectImage',
                documentName: rest.barcode,
              },
            })
            .catch(() => {}),
          this.props.getCustomerDetailItems(id).catch(() => {}),
          this.props.getProductAlternateItems(id).catch(() => {}),
          this.props.getSupplierDetailItems(id).catch(() => {}),
          this.props.getProductCategories({ productCategoryIds: rest.productCategoryIds }),
          this.props.getSuppliers(rest.defaultSupplierId ? { supplierIds: [rest.defaultSupplierId] } : undefined),
        ])

      let seed = Date.now() * -1

      this.setState(
        produce((draft) => {
          Object.assign(draft.item, rest)

          draft.image = image?.value?.data
          draft.customerDetailItems = get(customerDetailItems, 'value.data.items', []).map((each) => ({
            ...each,
            id: seed--,
            productId: id,
          }))
          draft.productAlternateItems = get(productAlternateItems, 'value.data.items', []).map((each) => ({
            ...each,
            id: seed--,
            productId: id,
          }))
          draft.supplierDetailItems = get(supplierDetailItems, 'value.data.items', []).map((each) => ({
            ...each,
            id: seed--,
            productId: id,
          }))
          draft.productCategories = get(productCategories, 'value.data.items', [])

          draft.copyFromExistingVisible = false
          draft.copyFromExistingBarcode = draft.copyFromExistingSelectedRecord.barcode
        })
      )
    } catch (error) {
      showError({ error })
    } finally {
      this.setState({ copyFromExistingLoading: false })
    }
  }

  render() {
    const { tenantTypeIsTrms, userIsDcribDistributor } = this.props
    const { item, fieldSettings, image, reworkedDescription } = this.state

    const isPrivateDatabase = this.getCatalogTableName() === PRIVATE_DATABASE

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const readOnly = isReadOnly(this) || getUserPermission('Products') !== 'Edit'

    return (
      <>
        <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
          {isPrivateDatabase && (
            <Dropdown
              overlay={
                <Menu onClick={this.handleActionsMenuClick}>
                  {!userIsDcribDistributor && [
                    <Menu.Item key="copyToInventory" disabled={!item.id}>
                      {t('copyToInventory')}
                    </Menu.Item>,
                    <Menu.Divider key="divider" />,
                  ]}
                  <Menu.Item key="copyFromExistingRecord" disabled={readOnly || Boolean(item.id)}>
                    {t('copyFromProductRecord')}
                  </Menu.Item>
                </Menu>
              }
              trigger={['click']}
            >
              <Button className="form-items-actions">
                {t('actions')}
                <Icon type="KeyboardArrowDown" />
              </Button>
            </Dropdown>
          )}
          <div className="form-items-container">
            <ValidationError errors={this.state.validationErrors} />
            {this.state.copyFromExistingBarcode && (
              <Alert
                className="mb-24"
                description={
                  !userIsDcribDistributor
                    ? t('copyFromExistingProductInfo')
                    : t('copyFromExistingProductInfoDistributor')
                }
                type="info"
                showIcon
              />
            )}
            <Row>
              {isPrivateDatabase && (
                <Col third>
                  <Form.Item label={createLabel('productCategoryIds')}>
                    <SelectProductCategories
                      value={item.productCategoryIds}
                      onChange={(value) => !readOnly && this.setItemValue('productCategoryIds', value)}
                      readOnly={readOnly}
                      dataSource={this.state.productCategories ?? []}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col third>
                <Form.Item label={createLabel('itemNumber')}>
                  {createFieldDecorator('itemNumber')(<Input readOnly={readOnly} />)}
                </Form.Item>
              </Col>
              <Col third>
                {isPrivateDatabase && (
                  <Form.Item label={createLabel('defaultSupplierId')}>
                    {createFieldDecorator('defaultSupplierId')(
                      <Select
                        dropdownRender={createDropdownRender(this.state.suppliers)}
                        onChange={(value) => this.setItemValue('defaultSupplierId', value || 0)}
                        onSearch={this.searchSuppliers}
                        readOnly={readOnly}
                        showSearch
                      >
                        <Option value={0}>{t('none')}</Option>
                        {(this.state.suppliers?.items ?? [])
                          .filter((each) => each.id)
                          .filter(createFilterUserSuppliers(item.defaultSupplierId))
                          .map((each) => (
                            <Option key={each.id} value={each.id}>
                              <span {...getOptionProps(each)}>{each.name}</span>
                            </Option>
                          ))}
                      </Select>
                    )}
                  </Form.Item>
                )}
              </Col>
            </Row>
            <Row>
              <Col third>
                <Form.Item label={createLabel('barcode')}>
                  {createFieldDecorator('barcode', { extraRules: [validateBarcode()] })(
                    <Input readOnly={readOnly} disabled={Boolean(item.id)} />
                  )}
                </Form.Item>
              </Col>
              <Col third>
                <Form.Item label={createLabel('barcode2')}>
                  {createFieldDecorator('barcode2', { extraRules: [validateBarcode()] })(<Input readOnly={readOnly} />)}
                </Form.Item>
              </Col>
              <Col third>
                <Form.Item label={createLabel('barcode3')}>
                  {createFieldDecorator('barcode3', { extraRules: [validateBarcode()] })(<Input readOnly={readOnly} />)}
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col twoThirds>
                <Form.Item label={createLabel('description')}>
                  {createFieldDecorator('description')(<Input readOnly={readOnly} disabled={item.isLinkedToCatalog} />)}
                </Form.Item>
              </Col>
              <Col third>
                <Form.Item label={createLabel('originCountryCode')}>
                  {createFieldDecorator('originCountryCode')(
                    <Select
                      onChange={(value) => this.setItemValue('originCountryCode', value || '')}
                      readOnly={readOnly}
                    >
                      <Option value="">{t('none')}</Option>
                      {getCountries(this.props.locale).map(([code, label]) => (
                        <Option key={code} value={code}>
                          {label}
                        </Option>
                      ))}
                    </Select>
                  )}
                </Form.Item>
              </Col>
            </Row>
            <Tabs
              activeKey={this.state.tabsActiveKey || 'general'}
              onChange={(tabsActiveKey) => this.setState({ tabsActiveKey })}
            >
              <Tabs.TabPane key="general" tab={t('general')} forceRender>
                <Row>
                  <Col third>
                    {isPrivateDatabase && (
                      <Form.Item label={createLabel('crossReferenceNumber')}>
                        {createFieldDecorator('crossReferenceNumber')(<Input readOnly={readOnly} />)}
                      </Form.Item>
                    )}
                    {isPrivateDatabase && (
                      <Form.Item label={createLabel('drawingNumber')}>
                        {createFieldDecorator('drawingNumber')(<Input readOnly={readOnly} />)}
                      </Form.Item>
                    )}
                    <Form.Item label={createLabel('manufacturer')}>
                      {createFieldDecorator('manufacturer')(<Input readOnly={readOnly} />)}
                    </Form.Item>
                    {isPrivateDatabase && (
                      <Form.Item label={createLabel('oldUnitOfMeasureId')}>
                        {createFieldDecorator('oldUnitOfMeasureId')(
                          <Select
                            onChange={(value) => this.setItemValue('oldUnitOfMeasureId', value || 0)}
                            readOnly={readOnly}
                          >
                            <Option value={0}>{t('none')}</Option>
                            {(this.state.unitsOfMeasure ?? [])
                              .filter((each) => each.id)
                              .map((each) => (
                                <Option key={each.id} value={each.id}>
                                  <span {...getOptionProps(each)}>{each.displayName}</span>
                                </Option>
                              ))}
                          </Select>
                        )}
                      </Form.Item>
                    )}
                    <Form.Item label={createLabel('packageUnitOfMeasure')}>
                      {createFieldDecorator('packageUnitOfMeasure')(
                        <Select
                          onChange={(value) => this.setItemValue('packageUnitOfMeasure', value || 0)}
                          readOnly={readOnly}
                        >
                          <Option value={0}>{t('none')}</Option>
                          {(this.state.unitsOfMeasure ?? [])
                            .filter((each) => each.id)
                            .map((each) => (
                              <Option key={each.id} value={each.id}>
                                <span {...getOptionProps(each)}>{each.displayName}</span>
                              </Option>
                            ))}
                        </Select>
                      )}
                    </Form.Item>
                  </Col>
                  <Col third>
                    {isPrivateDatabase && (
                      <Form.Item label={createLabel('accountNumber')}>
                        {createFieldDecorator('accountNumber')(<Input readOnly={readOnly} />)}
                      </Form.Item>
                    )}
                    {allowPricing() && (
                      <Form.Item label={createLabel('price')}>
                        {createFieldDecorator('price')(
                          <InputNumber
                            readOnly={readOnly}
                            onChange={(value) => this.setItemValue('price', value)}
                            disabled={item.isLinkedToCatalog}
                            min={0}
                            currency
                          />
                        )}
                      </Form.Item>
                    )}
                    <Form.Item label={createLabel('packageSize')}>
                      {createFieldDecorator('packageSize')(
                        <InputNumber
                          min={1}
                          readOnly={readOnly}
                          onChange={(value) => this.setItemValue('packageSize', Math.max(value, 1))}
                        />
                      )}
                    </Form.Item>
                    {allowPricing() && (
                      <Form.Item label={t('packagePrice')}>
                        <Input value={tryParseFloat(item.price) * item.packageSize} disabled />
                      </Form.Item>
                    )}
                    {isPrivateDatabase && (
                      <Form.Item label={createLabel('leadTime')}>
                        {createFieldDecorator('leadTime')(<InputNumber min={0} readOnly={readOnly} />)}
                      </Form.Item>
                    )}
                  </Col>
                  {isPrivateDatabase && (
                    <Col third>
                      <InputImage
                        label={t('productImage')}
                        onUpload={() => this.setState({ fileUploadDrawerVisible: true })}
                        onDelete={() => this.setState({ image: null })}
                        value={image}
                        readOnly={readOnly}
                      />
                      {item.isLinkedToCatalog && (
                        <Form.Item label={createLabel('sourceCatalogName')}>
                          <div className="tofino-label-buttons">
                            <Popconfirm
                              placement="topRight"
                              title={t('unlinkConfirmation')}
                              okText={t('yes')}
                              cancelText={t('no')}
                              onConfirm={() =>
                                this.setState(
                                  produce((draft) => {
                                    draft.item.isLinkedToCatalog = false
                                    draft.item.sourceCatalogTableName = ''
                                  })
                                )
                              }
                            >
                              <Tooltip title={t('unlink')}>
                                <Button size="small">
                                  <Icon type="LinkOff" />
                                </Button>
                              </Tooltip>
                            </Popconfirm>
                          </div>
                          <Input value={item.sourceCatalogName} disabled />
                        </Form.Item>
                      )}
                      <Row className="mb-12">
                        <Col>
                          {createFieldDecorator('taxable', { valuePropName: 'checked' })(
                            <Checkbox readOnly={readOnly}>{createLabel('taxable')}</Checkbox>
                          )}
                        </Col>
                      </Row>
                      <Row className="mb-12">
                        <Col>
                          {createFieldDecorator('nonInventory', { valuePropName: 'checked' })(
                            <Checkbox readOnly={readOnly}>{createLabel('nonInventory')}</Checkbox>
                          )}
                        </Col>
                      </Row>
                      <Row className="mb-12">
                        <Col>
                          {createFieldDecorator('managedItem', { valuePropName: 'checked' })(
                            <Checkbox readOnly={readOnly}>{createLabel('managedItem')}</Checkbox>
                          )}
                        </Col>
                      </Row>
                      <Row className="mb-12">
                        <Col>
                          {createFieldDecorator('certification1Required', {
                            valuePropName: 'checked',
                          })(<Checkbox readOnly={readOnly}>{createLabel('certification1Required')}</Checkbox>)}
                        </Col>
                      </Row>
                      <Row className="mb-12">
                        <Col>
                          {createFieldDecorator('expires', { valuePropName: 'checked' })(
                            <Checkbox readOnly={readOnly}>{createLabel('expires')}</Checkbox>
                          )}
                        </Col>
                      </Row>
                      <Row className="mb-12">
                        <Col>
                          {createFieldDecorator('active', { valuePropName: 'checked' })(
                            <Checkbox readOnly={readOnly}>{createLabel('active')}</Checkbox>
                          )}
                        </Col>
                      </Row>
                    </Col>
                  )}
                </Row>
                <Row>
                  <Col sm={isPrivateDatabase ? 12 : 24} xs={24}>
                    <Form.Item label={createLabel('keywords')}>
                      {createFieldDecorator('keywords')(<Input.TextArea rows={5} readOnly={readOnly} />)}
                    </Form.Item>
                  </Col>
                  <Col half>
                    {isPrivateDatabase && (
                      <Comments
                        value={item.customerComment}
                        onChange={(value) => this.setItemValue('customerComment', value)}
                        rows={5}
                        label={createLabel('customerComment')}
                        title={t('addComment')}
                        readOnly={readOnly}
                      />
                    )}
                  </Col>
                </Row>
              </Tabs.TabPane>
              {tenantTypeIsTrms && (
                <Tabs.TabPane key="supplierDetails" tab={t('supplierDetails')} forceRender>
                  {!!item && (
                    <SupplierDetails
                      parentRecord={item}
                      items={this.state.supplierDetailItems ?? []}
                      pageTotals={this.state.supplierDetailItemsTotals ?? {}}
                      onChange={(values) => this.setState({ supplierDetailItems: values })}
                      readOnly={readOnly}
                    />
                  )}
                </Tabs.TabPane>
              )}
              {userIsDcribDistributor && (
                <Tabs.TabPane key="customerDetails" tab={t('customerDetails')} forceRender>
                  <CustomerDetails
                    items={this.state.customerDetailItems ?? []}
                    pageTotals={this.state.customerDetailItemsTotals ?? {}}
                    onChange={(values) => this.setState({ customerDetailItems: values })}
                    readOnly={readOnly}
                  />
                </Tabs.TabPane>
              )}
              {isPrivateDatabase && (
                <Tabs.TabPane key="newRework" tab={t('newRework')} forceRender>
                  <Row gutter={24} className="mb-24">
                    <Col xs={2} sm={1}>
                      <Radio
                        id="reworkTypesNew"
                        checked={item.reworkType === reworkTypes.New}
                        onChange={(e) => this.setItemValue('reworkType', reworkTypes.New)}
                        readOnly={readOnly}
                      />
                    </Col>
                    <Col xs={22} sm={23}>
                      <Row className="mb-12">
                        <Col>
                          <label htmlFor="reworkTypesNew">{t('itemIsPurchasedNew')}</label>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row gutter={24} className="mb-12">
                    <Col xs={2} sm={1}>
                      <Radio
                        id="reworkTypesReworkable"
                        checked={item.reworkType === reworkTypes.Reworkable}
                        onChange={(e) => this.setItemValue('reworkType', reworkTypes.Reworkable)}
                        readOnly={readOnly}
                      />
                    </Col>
                    <Col xs={22} sm={23}>
                      <Row className="mb-12">
                        <Col>
                          <label htmlFor="reworkTypesReworkable">{t('itemIsReworkable')}</label>
                        </Col>
                      </Row>
                      <Form.Item label={createLabel('reworkedBarcode')}>
                        <Row>
                          <Col xs={18} sm={10}>
                            {createFieldDecorator('reworkedBarcode', {
                              extraRules: [validateBarcode()],
                            })(
                              <Input
                                readOnly={readOnly}
                                disabled={item.reworkType !== reworkTypes.Reworkable}
                                onChange={(e) => this.setItemValue('reworkedBarcode', e.target.value)}
                              />
                            )}
                          </Col>
                          <Col xs={6} sm={2}>
                            <Button
                              disabled={item.reworkType !== reworkTypes.Reworkable || isEmpty(item.reworkedBarcode)}
                              onClick={this.checkReworkedBarcode}
                            >
                              {t('check')}
                            </Button>
                          </Col>
                        </Row>
                      </Form.Item>
                      <Form.Item label={t('description')}>
                        <Input value={reworkedDescription} disabled />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={24} className="mb-24">
                    <Col xs={2} sm={1}>
                      <Radio
                        id="reworkTypesReworked"
                        checked={item.reworkType === reworkTypes.Reworked}
                        onChange={(e) => this.setItemValue('reworkType', reworkTypes.Reworked)}
                        readOnly={readOnly}
                      />
                    </Col>
                    <Col xs={22} sm={23}>
                      <div className="mb-12">
                        <label htmlFor="reworkTypesReworked">{t('itemIsReworked')}</label>
                      </div>
                      <div>
                        {createFieldDecorator('includeReworkInReplenishment', {
                          valuePropName: 'checked',
                        })(
                          <Checkbox disabled={item.reworkType !== reworkTypes.Reworked} readOnly={readOnly}>
                            {createLabel('includeReworkInReplenishment')}
                          </Checkbox>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Tabs.TabPane>
              )}
              {(tenantTypeIsTrms || userIsDcribDistributor) && (
                <Tabs.TabPane key="alternates" tab={t('alternates')} forceRender>
                  <ProductAlternates
                    items={this.state.productAlternateItems ?? []}
                    pageTotals={this.state.productAlternateItemsTotals ?? {}}
                    onChange={(values) => this.setState({ productAlternateItems: values })}
                    readOnly={readOnly}
                  />
                </Tabs.TabPane>
              )}
              {isPrivateDatabase && (
                <Tabs.TabPane key="documents" tab={t('documents')} forceRender>
                  <Documents
                    domainObjectId={item.id}
                    domainObjectType="Product"
                    items={this.state.documentItems ?? []}
                    pageTotals={this.state.documentItemsTotals ?? {}}
                    onChange={(values) => this.setState({ documentItems: values })}
                    readOnly={readOnly}
                  />
                </Tabs.TabPane>
              )}
            </Tabs>
          </div>
          <div className="form-buttons-container">
            <FormButtons
              readOnly={readOnly}
              onCancel={this.props.onCancel}
              onSubmit={this.handleSubmit}
              saveButtonLoading={this.state.saveButtonLoading}
              saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
            />
          </div>
        </Form>
        <Drawer
          title={t('selectFile')}
          size="xs"
          visible={this.state.fileUploadDrawerVisible}
          onClose={() => this.setState({ fileUploadDrawerVisible: false })}
        >
          <FileUpload
            accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif"
            onUpload={this.handleFileUpload}
            onCancel={() => this.setState({ fileUploadDrawerVisible: false })}
          />
        </Drawer>
        <Drawer
          title={t('copyToInventory')}
          size={linkTargets.inventoryRecord.formSize}
          visible={this.state.copyToInventoryVisible}
          onClose={() => this.setState({ copyToInventoryVisible: false })}
          saving={this.state.copyToInventorySaving}
        >
          <InventoryFormView
            selectedProduct={item}
            onSave={(pending) => this.setState({ copyToInventorySaving: pending })}
            onSaveAndClose={() => this.setState({ copyToInventoryVisible: false })}
            onCancel={() => this.setState({ copyToInventoryVisible: false })}
          />
        </Drawer>
        <Modal
          title={t('selectProduct')}
          visible={this.state.copyFromExistingVisible}
          okText={t('copy')}
          onOk={this.handleCopyFromExisting}
          okButtonProps={{
            loading: this.state.copyFromExistingLoading,
            disabled: !this.state.copyFromExistingSelectedRecord,
          }}
          onCancel={() => this.setState({ copyFromExistingVisible: false })}
          width={992}
        >
          <SelectProducts onSelect={(values) => this.setState({ copyFromExistingSelectedRecord: values[0] })} />
        </Modal>
        <Modal
          title={t('copyOptions')}
          visible={this.state.copyOptionsVisible}
          okText={this.state.copyOptions.newProductReplacesOriginalProduct ? t('applyChanges') : t('close')}
          okButtonProps={{
            loading: this.state.copyOptionsLoading,
            disabled:
              this.state.copyOptions.newProductReplacesOriginalProduct &&
              copyOptionsCheckboxes.every((one) => !this.state.copyOptions[one]),
          }}
          cancelButtonProps={{ style: { display: 'none' } }}
          onOk={
            this.state.copyOptions.newProductReplacesOriginalProduct
              ? this.state.copyOptionsOnOk
              : this.state.copyOptionsOnCancel
          }
          onCancel={this.state.copyOptionsOnCancel}
          width={576}
        >
          <div className="flex flex-row mb-12">
            <div>
              <Radio
                id="newProductDoesNotReplaceOriginalProduct"
                checked={!this.state.copyOptions.newProductReplacesOriginalProduct}
                onChange={(e) =>
                  this.setState(
                    produce((draft) => {
                      draft.copyOptions.newProductReplacesOriginalProduct = false
                    }),
                    this.saveState
                  )
                }
              />
            </div>
            <div>
              <label className="cursor-pointer" htmlFor="newProductDoesNotReplaceOriginalProduct">
                {t('newProductDoesNotReplaceOriginalProduct')}
              </label>
            </div>
          </div>
          <div className="flex flex-row mb-12">
            <div>
              <Radio
                id="newProductReplacesOriginalProduct"
                checked={this.state.copyOptions.newProductReplacesOriginalProduct}
                onChange={(e) =>
                  this.setState(
                    produce((draft) => {
                      draft.copyOptions.newProductReplacesOriginalProduct = true
                    }),
                    this.saveState
                  )
                }
              />
            </div>
            <div>
              <div className="mb-12">
                <label className="cursor-pointer" htmlFor="newProductReplacesOriginalProduct">
                  {t('newProductReplacesOriginalProduct')}
                </label>
              </div>
              {copyOptionsCheckboxes.map((each) => (
                <div key={each} className="flex flex-row mb-12">
                  <div>
                    <Checkbox
                      checked={this.state.copyOptions[each]}
                      onChange={(e) =>
                        this.setState(
                          produce((draft) => {
                            draft.copyOptions[each] = e.target.checked
                          }),
                          this.saveState
                        )
                      }
                      disabled={!this.state.copyOptions.newProductReplacesOriginalProduct}
                    >
                      {t(each)}{' '}
                      {each === 'updateExistingInventoryRecords' && (
                        <Help title={t('updateExistingInventoryRecordsInfo')} />
                      )}
                    </Checkbox>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      </>
    )
  }
}

export default Form.create()(FormView)
